import { Button, MessageSquareMoreIcon, EmptyState } from '@buffer-mono/popcorn'

import { debugLog } from '~publish/helpers/debugLog'

import { DEFAULT_STATUS } from '../../../CommunityFilters/types'
import type { CommentStatus } from '../../../graphqlEnums'
import { isGoogleBusinessProfile } from '../../../util/channelHelpers'
import type { EmptyStateChannel } from './types'

import type { JSX } from 'react'

type StatusMessage = {
  heading: string
  description: string
}

const DEFAULT_MESSAGE: StatusMessage = {
  heading: 'No comments found',
  description: 'There are no comments to display at this time.',
}

const DEFAULT_MESSAGE_GBP: StatusMessage = {
  heading: 'No reviews found',
  description: 'There are no reviews to display at this time.',
}

const STATUS_MESSAGES: Partial<Record<CommentStatus | 'all', StatusMessage>> = {
  all: {
    heading: 'No comments yet',
    description: 'We will let you know when new comments appear.',
  },
  dismissed: {
    heading: "You haven't resolved any comments yet.",
    description: 'This is where those comments will appear.',
  },
  replied: {
    heading: "You haven't replied to any comments yet.",
    description: 'This is where those comments will appear.',
  },
  unreplied: {
    heading: 'No comments found',
    description: 'There are no comments to display at this time.',
  },
  new: {
    heading: 'No new comments',
    description: 'There are no new comments to display.',
  },
}

const STATUS_MESSAGES_GBP: Partial<
  Record<CommentStatus | 'all', StatusMessage>
> = {
  all: {
    heading: 'No reviews yet',
    description: 'We will let you know when new reviews appear.',
  },
  dismissed: {
    heading: "You haven't resolved any reviews yet.",
    description: 'This is where those reviews will appear.',
  },
  replied: {
    heading: "You haven't replied to any reviews yet.",
    description: 'This is where those reviews will appear.',
  },
  unreplied: {
    heading: 'No reviews found',
    description: 'There are no reviews to display at this time.',
  },
  new: {
    heading: 'No new reviews',
    description: 'There are no new reviews to display.',
  },
}

const FILTERED_MESSAGE: StatusMessage = {
  heading: 'No comments found',
  description: 'There are no comments meeting your filtering criteria.',
}

const FILTERED_MESSAGE_GBP: StatusMessage = {
  heading: 'No reviews found',
  description: 'There are no reviews meeting your filtering criteria.',
}

interface NoCommentsFoundProps {
  status?: CommentStatus | 'all'
  hasActiveFilters: boolean
  onClearFilters: () => void
  onNavigateToUnreplied?: () => void
  channels?: EmptyStateChannel[]
}

export const NoCommentsFound = ({
  status = DEFAULT_STATUS,
  hasActiveFilters,
  onClearFilters,
  onNavigateToUnreplied,
  channels,
}: NoCommentsFoundProps): JSX.Element => {
  const isGBP = isGoogleBusinessProfile(channels)
  const selectedStatusMessages = isGBP ? STATUS_MESSAGES_GBP : STATUS_MESSAGES
  const selectedDefaultMessage = isGBP ? DEFAULT_MESSAGE_GBP : DEFAULT_MESSAGE
  const selectedFilteredMessage = isGBP
    ? FILTERED_MESSAGE_GBP
    : FILTERED_MESSAGE

  const message = hasActiveFilters
    ? selectedFilteredMessage
    : selectedStatusMessages[status] || selectedDefaultMessage

  if (
    !selectedStatusMessages[status] &&
    process.env.NODE_ENV === 'development'
  ) {
    debugLog(
      `NoCommentsFound: No message defined for status "${status}". Using default message.`,
    )
  }

  return (
    <EmptyState size="large" variant="neutral">
      <EmptyState.Icon>
        <MessageSquareMoreIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>{message.heading}</EmptyState.Heading>
      <EmptyState.Description>{message.description}</EmptyState.Description>
      {hasActiveFilters && (
        <EmptyState.Actions>
          <Button size="large" onClick={onClearFilters}>
            Reset Filters
          </Button>
        </EmptyState.Actions>
      )}
      {!hasActiveFilters &&
        (status === 'replied' || status === 'dismissed') &&
        onNavigateToUnreplied && (
          <EmptyState.Actions>
            <Button size="large" onClick={onNavigateToUnreplied}>
              {isGBP ? 'Go to Unanswered Reviews' : 'Go to Unanswered Comments'}
            </Button>
          </EmptyState.Actions>
        )}
    </EmptyState>
  )
}
